import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { toast } from 'sonner'
import { createQueryErrorHandler } from '@/lib/error-handling'

export function getContext() {
  // Create enhanced error handling
  const { queryCache, mutationCache } = createQueryErrorHandler({
    showUserNotification: (message, type) => {
      switch (type) {
        case 'error':
          toast.error(message)
          break
        case 'warning':
          toast.warning(message)
          break
        default:
          toast(message)
      }
    },
    onAuthenticationError: () => {
      // Don't show toast for 401 errors
      // The AuthGuard component will handle redirecting to login
      // Showing a toast here causes false positives during navigation
      // and the redirect happens anyway
    },
  })

  const queryClient = new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          // Don't retry on 4xx errors
          const status = (error as any)?.status
          if (status && status >= 400 && status < 500) return false
          return failureCount < 2 // Reduced from 3 to 2
        },
        retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 5000), // Faster retries
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        refetchOnWindowFocus: false,
        refetchOnReconnect: 'always', // Better for PWA
        networkMode: 'offlineFirst', // Better for PWA
      },
      mutations: {
        retry: 1,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
        networkMode: 'offlineFirst',
      },
    },
  })

  return {
    queryClient,
  }
}

export function Provider({
  children,
  queryClient,
}: {
  children: React.ReactNode
  queryClient: QueryClient
}) {
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  )
}
